import React, { useEffect, useState } from "react";
import {
  AiOutlineMail,
  AiOutlinePhone,
  AiOutlineLock,
  AiOutlineEye,
  AiOutlineEyeInvisible,
} from "react-icons/ai";
import { FaUserTie } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
  createTeamUser,
  verifyTeamOTP,
  resetAuthState,
} from "../features/auth/authSlice";

const addTeamSchema = yup.object().shape({
  firstname: yup.string().required("First name is required"),
  lastname: yup.string().required("Last name is required"),
  email: yup.string().email("Email should be valid").required("Email is required"),
  mobile: yup.string().required("Mobile number is required"),
  role: yup.string().required("Role is required"),
  password: yup.string().required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords do not match")
    .required("Confirm password is required"),
});

const otpSchema = yup.object().shape({
  otp: yup
    .string()
    .matches(/^\d{6}$/, "OTP must be exactly 6 digits")
    .required("OTP is required"),
});

const AddTeam = () => {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const { isLoading, isSuccess, isError, message, otpEmail } = authState;
  const [otpTimer, setOtpTimer] = useState(120);
  const [pendingUserData, setPendingUserData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtpForm, setShowOtpForm] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      email: "",
      mobile: "",
      password: "",
      confirmPassword: "",
      role: "employee",
    },
    validationSchema: addTeamSchema,
    onSubmit: (values) => {
      const payload = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        mobile: values.mobile,
        password: values.password,
        role: values.role,
        agentName: values.firstname,
      };

      setPendingUserData(payload);
      dispatch(createTeamUser(payload));
    },
  });

  const otpFormik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: otpSchema,
    onSubmit: (values) => {
      dispatch(verifyTeamOTP(values));
    },
  });

const handleResendOtp = () => {
  if (!pendingUserData) {
    toast.error("No team member data found to resend OTP.");
    return;
  }

  dispatch(createTeamUser(pendingUserData));
  setOtpTimer(120);
};

useEffect(() => {
  if (isSuccess && message === "OTP has been sent to the team member's email") {
    toast.info("OTP sent successfully. Ask the team member for the 6-digit code.");
    setShowOtpForm(true);
    setOtpTimer(120);
    dispatch(resetAuthState());
  }
}, [isSuccess, message, dispatch]);

useEffect(() => {
  if (isSuccess && message === "Team member created successfully") {
    toast.success("Team member has been added successfully");
    formik.resetForm();
    otpFormik.resetForm();
    setShowOtpForm(false);
    setOtpTimer(120);
    setPendingUserData(null);
    dispatch(resetAuthState());
  }
}, [isSuccess, message, dispatch]);

  useEffect(() => {
    if (isError && message) {
      toast.error(typeof message === "string" ? message : "Something went wrong");
      dispatch(resetAuthState());
    }
  }, [isError, message, dispatch]);

  useEffect(() => {
  let interval;

  if (showOtpForm && otpTimer > 0) {
    interval = setInterval(() => {
      setOtpTimer((prev) => prev - 1);
    }, 1000);
  }

  return () => {
    if (interval) clearInterval(interval);
  };
}, [showOtpForm, otpTimer]);

const handleBackToForm = () => {
  setShowOtpForm(false);
  otpFormik.resetForm();
  dispatch(resetAuthState());
};

  return (
    <div className="container-fluid px-0">
      <div className="mb-4">
        <h3 className="title mb-1">Add Team Member</h3>
        <p className="text-muted mb-0">
          Create a new admin or employee account for the CRM team.
        </p>
      </div>

      <div
        className="bg-white rounded-4 shadow-sm overflow-hidden"
        style={{
          maxWidth: "900px",
          border: "1px solid #eaecf0",
        }}
      >
        <div className="p-4 p-md-5">
          <div className="mb-4">
            <h5 className="fw-bold mb-1">Team Member Details</h5>
            <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
              Fill in the information below to create a new account.
            </p>
          </div>

          {!showOtpForm && (
            <form onSubmit={formik.handleSubmit}>
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">First Name</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <FaUserTie />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      name="firstname"
                      placeholder="Enter first name"
                      value={formik.values.firstname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.firstname && formik.errors.firstname && (
                    <small className="text-danger">{formik.errors.firstname}</small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Last Name</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <FaUserTie />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      name="lastname"
                      placeholder="Enter last name"
                      value={formik.values.lastname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.lastname && formik.errors.lastname && (
                    <small className="text-danger">{formik.errors.lastname}</small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Email Address</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <AiOutlineMail />
                    </span>
                    <input
                      type="email"
                      className="form-control border-start-0"
                      name="email"
                      placeholder="Enter email address"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <small className="text-danger">{formik.errors.email}</small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Mobile Number</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <AiOutlinePhone />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      name="mobile"
                      placeholder="Enter mobile number"
                      value={formik.values.mobile}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.mobile && formik.errors.mobile && (
                    <small className="text-danger">{formik.errors.mobile}</small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Role</label>
                  <select
                    className="form-select"
                    name="role"
                    value={formik.values.role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Password</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <AiOutlineLock />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control border-start-0 border-end-0"
                      name="password"
                      placeholder="Enter password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <button
                      type="button"
                      className="input-group-text bg-light border-start-0"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <small className="text-danger">{formik.errors.password}</small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Confirm Password</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <AiOutlineLock />
                    </span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-control border-start-0 border-end-0"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <button
                      type="button"
                      className="input-group-text bg-light border-start-0"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      {showConfirmPassword ? (
                        <AiOutlineEyeInvisible />
                      ) : (
                        <AiOutlineEye />
                      )}
                    </button>
                  </div>
                  {formik.touched.confirmPassword &&
                    formik.errors.confirmPassword && (
                      <small className="text-danger">
                        {formik.errors.confirmPassword}
                      </small>
                    )}
                </div>
              </div>

              <div className="d-flex justify-content-end mt-5 pt-2 border-top">
                <button
                  type="submit"
                  className="btn px-4 py-2 fw-semibold"
                  style={{
                    background: "#000000",
                    color: "#fff",
                    borderRadius: "12px",
                    minWidth: "180px",
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? "Sending OTP..." : "Send OTP"}
                </button>
              </div>
            </form>
          )}


      {showOtpForm && (
        <div
          className="rounded-4 p-4 p-md-5"
          style={{
            background: "#fcfcfd",
            border: "1px solid #eaecf0",
          }}
        >
          <div className="text-center mb-4">
            <div
              className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: 64,
                height: 64,
                background: "#f2f4f7",
                color: "#101828",
                fontSize: "24px",
                fontWeight: 700,
              }}
            >
              OTP
            </div>

            <h5 className="fw-bold mb-1">Verify Email OTP</h5>
            <p
              className="text-muted mb-2"
              style={{ maxWidth: "520px", margin: "0 auto" }}
            >
              A 6-digit verification code has been sent to{" "}
              <strong>{otpEmail}</strong>. Enter it below to complete team member
              creation.
            </p>

            <div
              className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
              style={{
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                color: "#9a3412",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              OTP expires in <span>{otpTimer}s</span>
            </div>
          </div>

          <form onSubmit={otpFormik.handleSubmit}>
            <div className="mb-4">
              <label className="form-label fw-semibold">OTP Code</label>
              <input
                type="text"
                className="form-control text-center fw-semibold"
                name="otp"
                placeholder="Enter 6-digit OTP"
                value={otpFormik.values.otp}
                onChange={otpFormik.handleChange}
                onBlur={otpFormik.handleBlur}
                maxLength={6}
                style={{
                  height: "54px",
                  fontSize: "20px",
                  letterSpacing: "8px",
                  borderRadius: "12px",
                }}
              />
              {otpFormik.touched.otp && otpFormik.errors.otp && (
                <small className="text-danger">{otpFormik.errors.otp}</small>
              )}
            </div>

          <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-3">
            <div className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center">
              <button
                type="button"
                className="btn btn-light fw-semibold px-4 py-2"
                style={{
                  borderRadius: "12px",
                  border: "1px solid #d0d5dd",
                  minWidth: "140px",
                }}
                onClick={handleBackToForm}
                disabled={isLoading}
              >
                Back
              </button>

              <button
                type="button"
                className="btn btn-light fw-semibold px-4 py-2"
                style={{
                  borderRadius: "12px",
                  border: "1px solid #d0d5dd",
                  minWidth: "180px",
                }}
                disabled={isLoading}
                onClick={handleResendOtp}
              >
                Resend OTP
              </button>
            </div>

            <button
              type="submit"
              className="btn px-4 py-2 fw-semibold"
              style={{
                background: "#000000",
                color: "#fff",
                borderRadius: "12px",
                minWidth: "180px",
              }}
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
                   
          </form>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default AddTeam;